import React from "react";
import { FedexTrackingResponse } from "../../models/Tracking";
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

const getStatusColor = (statusCode: string) => {
  switch (statusCode.toLowerCase()) {
    case "dl":
      return "success";
    case "it":
    case "ar":
    case "dp":
      return "processing";
    case "ex":
      return "error";
    default:
      return "default";
  }
};
const getEventIcon = (statusCode: string) => {
  const code = statusCode?.toUpperCase();

  switch (code) {
    case "IN": // Initiated
      return <InboxOutlined style={{ color: "#1890ff" }} />; // mới tạo đơn
    case "PU": // Picked up
      return <EnvironmentOutlined style={{ color: "#722ed1" }} />;
    case "IT": // In transit
    case "DP": // Departed FedEx location
    case "AR": // Arrived FedEx location
      return <LoadingOutlined style={{ color: "#1890ff" }} />;
    case "OD": // Out for delivery
      return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    case "DL": // Delivered
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    case "HL": // Hold
    case "HP": // Hold for pickup
      return <StopOutlined style={{ color: "#fa541c" }} />;
    case "RS": // Return to sender
      return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
    case "EX": // Exception
    case "FD": // Delivery failed
    case "SE": // Shipment exception
      return <InfoCircleOutlined style={{ color: "#faad14" }} />;
    default:
      return <InfoCircleOutlined style={{ color: "#bfbfbf" }} />;
  }
};
interface FedexTrackingProps {
  error: any;
  isLoading: boolean;
  shipmentData: FedexTrackingResponse | undefined;
}

const FedexTracking = ({
  error,
  isLoading,
  shipmentData,
}: FedexTrackingProps) => {
  const trackResult =
    shipmentData?.output?.completeTrackResults?.[0]?.trackResults?.[0];

  return (
    <>
      <Col xs={24} lg={10}>
        {error && (
          <Alert
            message="Lỗi"
            description="Không thể tra cứu vận đơn. Vui lòng kiểm tra lại số vận đơn hoặc thử lại sau."
            type="error"
            showIcon
            className="mb-4"
          />
        )}
        {isLoading && (
          <Card bordered={false} className="shadow-sm">
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        )}

        {trackResult && (
          <Card bordered={false} className="shadow-sm">
            <Descriptions
              title={
                <Space className="mb-4">
                  <Text strong style={{ fontSize: 16 }}>
                    Thông tin vận đơn
                  </Text>
                  <Tag
                    color={getStatusColor(
                      trackResult.latestStatusDetail.derivedCode
                    )}
                  >
                    {trackResult.latestStatusDetail.statusByLocale}
                  </Tag>
                </Space>
              }
              column={1}
              bordered
              size="small"
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Mã vận đơn">
                <Text copyable>
                  {trackResult.trackingNumberInfo.trackingNumber}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Dịch vụ">
                {trackResult.serviceDetail?.description}
              </Descriptions.Item>
              <Descriptions.Item label="Điểm đi">
                <Space>
                  <EnvironmentOutlined className="text-blue-500" />
                  {trackResult.shipperInformation?.address?.city},{" "}
                  {trackResult.shipperInformation?.address?.countryCode}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Điểm đến">
                <Space>
                  <EnvironmentOutlined className="text-red-500" />
                  {trackResult.recipientInformation?.address?.city},{" "}
                  {trackResult.recipientInformation?.address?.countryCode}
                </Space>
              </Descriptions.Item>
              {trackResult.dateAndTimes?.map((dateTime) => {
                let label = "";
                switch (dateTime.type) {
                  case "ACTUAL_DELIVERY":
                    label = "Thời gian giao hàng";
                    break;
                  case "ACTUAL_PICKUP":
                    label = "Thời gian lấy hàng";
                    break;
                  case "ACTUAL_TENDER":
                    label = "Thời gian nhận hàng";
                    break;
                  case "ANTICIPATED_TENDER":
                    label = "Dự kiến nhận hàng";
                    break;
                  case "SHIP":
                    label = "Thời gian gửi hàng";
                    break;
                  case "ESTIMATED_DELIVERY":
                    label = "Dự kiến giao hàng";
                    break;
                  default:
                    return null;
                }
                return (
                  <Descriptions.Item key={dateTime.type} label={label}>
                    <Space>
                      <ClockCircleOutlined className="text-blue-500" />
                      {dayjs(dateTime.dateTime).format("DD/MM/YYYY HH:mm")}
                    </Space>
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </Card>
        )}
      </Col>

      <Col xs={24} lg={14}>
        {trackResult && (
          <Card
            bordered={false}
            className="shadow-sm h-full"
            title={
              <Space>
                <ClockCircleOutlined />
                <Text strong>Lịch sử vận chuyển</Text>
              </Space>
            }
          >
            <Timeline
              mode="left"
              items={trackResult.scanEvents.map((event) => ({
                dot: getEventIcon(event.derivedStatusCode),
                children: (
                  <div className="pb-6">
                    <Text strong className="block text-base">
                      {event.eventDescription}
                    </Text>
                    <Text strong className="block text-base">
                      {event.exceptionDescription && (
                        <Tag color="red">{event.exceptionDescription}</Tag>
                      )}
                    </Text>
                    <Space className="mt-2">
                      <Tag icon={<ClockCircleOutlined />}>
                        {dayjs(event.date).format("DD/MM/YYYY HH:mm")}
                      </Tag>

                      <Tag icon={<EnvironmentOutlined />}>
                        {event.scanLocation.city},{" "}
                        {event.scanLocation.countryCode}
                      </Tag>
                    </Space>
                  </div>
                ),
              }))}
            />
          </Card>
        )}
      </Col>
    </>
  );
};

export default FedexTracking;
